import { AfterViewInit, Component, ErrorHandler, HostListener, Inject, OnInit, ViewChild, ChangeDetectionStrategy, Injector, computed, inject, signal } from '@angular/core'
import { FieldTree, form, required, validate } from '@angular/forms/signals'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatInput } from '@angular/material/input'

import { arvonMuutokset } from './_jaettu-angular/_lomakkeet/arvon-muutokset'
import { validoiReaktiivisesti } from './_jaettu-angular/_lomakkeet/reaktiivinen-validaattori'
import { FormValidationService } from './_jaettu-angular/service/form-validation.service'
import { LadataanService } from './_jaettu-angular/service/ladataan.service'
import { LemonTranslationService } from './_jaettu-angular/service/lemon-translation.service'

import { VaihdaSalasanaPyynto, VaihdaSalasanaVastaus, VaihdaSalasanaVirhe } from '@lemon/core-firebase/model/common'
import { LemonaidValidators } from './_angular/_validator/LemonaidValidators'
import { FirebaseLemonaid } from './_angular/service/firebase-lemonaid.service'

export interface VaihdaSalasanaDialogData {}

interface SalasanaLomake {
  oldpassword: string
  newpassword: string
}

@Component({
  templateUrl: './vaihda-salasana.dialog.html',
  styleUrls: ['./vaihda-salasana.dialog.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class VaihdaSalasanaDialog implements OnInit, AfterViewInit {
  @ViewChild('oldpasswordInput', { read: MatInput }) oldpasswordInput: MatInput

  private readonly _injector = inject(Injector)
  private readonly _vaaraSalasana = signal(false)

  lomake = form(signal<SalasanaLomake>({ oldpassword: '', newpassword: '' }), polku => {
    required(polku.oldpassword)
    validate(polku.oldpassword, () => (this._vaaraSalasana() ? { kind: 'wrongpassword' } : null))
    required(polku.newpassword)
    validoiReaktiivisesti(polku.newpassword, LemonaidValidators.passwordComplexityValidator)
  })
  readonly commonError = signal<string>(undefined)
  readonly success = signal(false)
  readonly virheet = computed(() => this._annaVirheet(this.lomake.newpassword))
  uusiSalasana: string = null
  vanhaSalasana: string = null

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: VaihdaSalasanaDialogData,
    private dialogRef: MatDialogRef<VaihdaSalasanaDialog>,
    private _firebaseLemonaid: FirebaseLemonaid,
    private validationService: FormValidationService,
    private errorHandler: ErrorHandler,
    private ladataanService: LadataanService,
    private translationService: LemonTranslationService,
  ) {}

  ngOnInit(): void {
    arvonMuutokset(this.lomake.oldpassword, { injector: this._injector }).subscribe(value => {
      this._vaaraSalasana.set(false)
      this.vanhaSalasana = value
    })
    arvonMuutokset(this.lomake.newpassword, { injector: this._injector }).subscribe(value => {
      this.uusiSalasana = value
    })
  }

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }

  onkoMuokattuTaiKosketettu<T>(kentta: FieldTree<T>): boolean {
    return kentta().dirty() || kentta().touched()
  }

  private _annaVirheet<T>(kentta: FieldTree<T>) {
    const virheet: Record<string, boolean> = {}
    for (const virhe of kentta().errors()) {
      virheet[virhe.kind] = true
    }
    return virheet
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.oldpasswordInput.focus()
    }, 0)
  }

  @HostListener('document:keydown.enter')
  defaultAction() {
    if (!this.success()) {
      this.changePassword()
    } else {
      this.dialogRef.close()
    }
  }

  changePassword() {
    if (!this.lomake().valid()) {
      this.validationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }

    this.ladataanService.aloitaLataaminen()

    const data: VaihdaSalasanaPyynto = {
      uusiSalasana: this.lomake.newpassword().value(),
      vanhaSalasana: this.lomake.oldpassword().value(),
    }

    this._firebaseLemonaid
      .functionsCall<VaihdaSalasanaPyynto, VaihdaSalasanaVastaus>('vaihdaSalasana', data)
      .then(vastaus => {
        this.commonError.set('')
        if ((vastaus.virheet && vastaus.virheet.length > 0) || vastaus.virhekoodi) {
          if (vastaus.virhekoodi) {
            this.kasitteleVirhe({ code: vastaus.virhekoodi })
          }
          if (vastaus.virheet && vastaus.virheet.length > 0) {
            this.kasitteleOmaVirhe(vastaus.virheet)
          }
        } else {
          this.success.set(true)
        }
        this.ladataanService.lopetaLataaminen()
      })
      .catch(err => {
        this.kasitteleVirhe(err)
        this.ladataanService.lopetaLataaminen()
      })
  }

  private kasitteleOmaVirhe(errors: VaihdaSalasanaVirhe[]) {
    if (errors && errors.length > 0) {
      for (const error of errors) {
        if (error === VaihdaSalasanaVirhe.UUSI_SALASANA_EI_SISALLA_NUMEROA) {
          this.commonError.update(virhe => virhe + this.translationService.lokalisoi('reset.validation.number') + ' ')
        } else if (error === VaihdaSalasanaVirhe.UUSI_SALASANA_EI_SISALLA_PIENTA_KIRJAINTA) {
          this.commonError.update(virhe => virhe + this.translationService.lokalisoi('reset.validation.lettersmall') + ' ')
        } else if (error === VaihdaSalasanaVirhe.UUSI_SALASANA_EI_SISALLA_SUURTA_KIRJAINTA) {
          this.commonError.update(virhe => virhe + this.translationService.lokalisoi('reset.validation.letterbig') + ' ')
        } else if (error === VaihdaSalasanaVirhe.UUSI_SALASANA_LIIAN_LYHYT) {
          this.commonError.update(virhe => virhe + this.translationService.lokalisoi('reset.validation.min') + ' ')
        } else if (error === VaihdaSalasanaVirhe.UUSI_SALASANA_LIIAN_VAHAN_YKSILOLLISIA_MERKKEJÄ) {
          this.commonError.update(virhe => virhe + this.translationService.lokalisoi('reset.validation.differentchars') + ' ')
        } else if (error === VaihdaSalasanaVirhe.UUSI_SALASANA_PUUTTUU) {
          // EI VOI KOSKAAN TAPAHTUA?
        } else if (error === VaihdaSalasanaVirhe.VANHA_SALASANA_PUUTTUU) {
          // EI VOI KOSKAAN TAPAHTUA?
        }
      }
    }
  }

  private kasitteleVirhe(err: any) {
    const code = err.code
    if (code) {
      switch (code) {
        case 'auth/expired-action-code':
          this.commonError.set(this.translationService.lokalisoi('reset.virheet.koodi-vanhentunut'))
          break
        case 'auth/invalid-action-code':
          this.commonError.set(this.translationService.lokalisoi('reset.virheet.koodi-ei-oikea'))
          break
        case 'auth/user-disabled':
          this.commonError.set(this.translationService.lokalisoi('reset.virheet.kayttaja-inaktiivinen'))
          break
        case 'auth/user-not-found':
          this.commonError.set(this.translationService.lokalisoi('reset.virheet.kayttaja-ei-loydy'))
          break
        case 'auth/weak-password':
          this.commonError.set(this.translationService.lokalisoi('reset.virheet.salasana-heikko'))
          break
        case 'auth/wrong-password':
          this._vaaraSalasana.set(true)
          break
        default:
          this.commonError.set(this.translationService.lokalisoi('yleiset.tuntematon-virhe'))
          this.errorHandler.handleError(err)
      }
      this.validationService.merkitseKokoLomakeKosketuksi(this.lomake)
    } else {
      this.commonError.set(this.translationService.lokalisoi('yleiset.tuntematon-virhe'))
      this.errorHandler.handleError(err)
    }
  }
}
